import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  FileText, 
  TrendingUp, 
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  ChevronRight,
  Zap,
  Star,
  FileSearch,
  MessagesSquare,
  BarChart3,
  Wrench
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import DashboardLayout from "../../components/DashboardLayout";
import PendingExternalPayments from "../../components/Admin/PendingExternalPayments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [foiaCount, setFoiaCount] = useState(0);
  const [chatCount, setChatCount] = useState(0);
  const [repairing, setRepairing] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchFOIACount();
    fetchChatCount();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`);
      setStats(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFOIACount = async () => {
    try {
      const res = await axios.get(`${API}/admin/foia/requests`);
      const pending = res.data.filter(r => r.status === "pending").length;
      setFoiaCount(pending);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChatCount = async () => {
    try {
      const res = await axios.get(`${API}/admin/conversations`);
      const unread = res.data.filter(c => c.unread_by_admin).length;
      setChatCount(unread);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRepairAllCases = async () => {
    if (!window.confirm("¿Estás seguro de que deseas reparar los pasos de todos los casos? Esto creará los 6 pasos del 'Camino al Éxito' para todos los casos que no los tengan.")) {
      return;
    }
    
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/admin/repair-all-cases-steps`);
      toast.success(`✅ ${res.data.message}`);
      console.log("Repair result:", res.data);
    } catch (error) {
      console.error("Repair error:", error);
      toast.error("Error al reparar los casos: " + (error.response?.data?.detail || error.message));
    } finally {
      setRepairing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: "Total Casos",
      value: stats?.total_cases || 0,
      icon: FileText,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: "Casos Activos",
      value: stats?.active_cases || 0,
      icon: Clock,
      color: "bg-amber-100 text-amber-600"
    },
    {
      title: "Tasa de Aprobación",
      value: `${stats?.approval_rate || 0}%`,
      icon: TrendingUp,
      color: "bg-emerald-100 text-emerald-600"
    },
    {
      title: "Alto Riesgo",
      value: stats?.high_risk_cases || 0,
      icon: AlertTriangle,
      color: "bg-red-100 text-red-600"
    },
    {
      title: "Optimización Activa",
      value: stats?.monitoring_active || 0,
      icon: Zap,
      color: "bg-purple-100 text-purple-600"
    },
    {
      title: "Próximas Entrevistas",
      value: stats?.upcoming_interviews || 0,
      icon: Calendar,
      color: "bg-indigo-100 text-indigo-600"
    }
  ];

  const getStepColor = (step) => {
    const colors = {
      red: "bg-red-500",
      yellow: "bg-amber-500",
      blue: "bg-blue-500",
      green: "bg-emerald-500",
      gold: "bg-yellow-400",
      purple: "bg-purple-500"
    };
    return colors[step?.color] || colors.red;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
              Panel de Administración
            </h1>
            <p className="text-navy-600">Gestión del Camino al Éxito - 6 Etapas</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRepairAllCases}
              disabled={repairing}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="repair-cases-btn"
            >
              <Wrench className="w-4 h-4 mr-2" />
              {repairing ? "Reparando..." : "Reparar Pasos"}
            </Button>
            <Link to="/admin/cases">
              <Button className="bg-gold-600 hover:bg-gold-700 text-white" data-testid="view-all-cases-btn">
                Ver todos los casos
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card-elevated p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-2xl font-bold text-navy-900">{stat.value}</p>
              <p className="text-xs text-navy-500 mt-1">{stat.title}</p>
            </motion.div>
          ))}
        </div>

        {/* Step Colors Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-elevated p-4"
        >
          <h3 className="font-semibold text-navy-900 mb-3">Leyenda de Colores - Camino al Éxito</h3>
          <div className="flex flex-wrap gap-4">
            {[
              { color: "bg-red-500", label: "Pendiente" },
              { color: "bg-amber-500", label: "En progreso" },
              { color: "bg-blue-500", label: "En revisión" },
              { color: "bg-emerald-500", label: "Completado" },
              { color: "bg-yellow-400", label: "Hito logrado" },
              { color: "bg-purple-500", label: "Optimización activa" }
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                <span className="text-sm text-navy-600">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pending External Payments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <PendingExternalPayments />
        </motion.div>

        {/* Recent Cases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-elevated overflow-hidden"
        >
          <div className="p-6 border-b border-navy-100">
            <h2 className="font-heading text-xl font-bold text-navy-900">Casos Recientes</h2>
          </div>
          <div className="divide-y divide-navy-100">
            {stats?.recent_cases?.slice(0, 5).map((caseItem) => (
              <Link
                key={caseItem.id}
                to={`/admin/cases/${caseItem.id}`}
                className="flex items-center justify-between p-4 hover:bg-navy-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-gold-700">
                      {caseItem.user?.name?.[0] || "?"}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-navy-900">{caseItem.user?.name || "Usuario"}</p>
                    <p className="text-sm text-navy-500">{caseItem.visa_type} - Etapa {caseItem.current_step}/9</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Mini progress dots */}
                  <div className="hidden sm:flex items-center gap-1">
                    {[1, 2, 3, 4, 5, 6].map(step => {
                      const stepData = caseItem.steps?.find(s => s.step_order === step);
                      return (
                        <div 
                          key={step} 
                          className={`w-2 h-2 rounded-full ${getStepColor(stepData)}`}
                        ></div>
                      );
                    })}
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      caseItem.risk_level === "low" ? "text-emerald-600" :
                      caseItem.risk_level === "medium" ? "text-amber-600" :
                      caseItem.risk_level === "high" ? "text-red-600" : "text-navy-500"
                    }`}>
                      {caseItem.risk_level === "low" ? "Bajo riesgo" :
                       caseItem.risk_level === "medium" ? "Riesgo medio" :
                       caseItem.risk_level === "high" ? "Alto riesgo" : "Pendiente"}
                    </div>
                    <p className="text-sm text-navy-400">{caseItem.progress_percentage}%</p>
                  </div>
                  {caseItem.monitoring_active && (
                    <Zap className="w-4 h-4 text-purple-500" />
                  )}
                  <ChevronRight className="w-5 h-5 text-navy-400" />
                </div>
              </Link>
            ))}
          </div>
          {(!stats?.recent_cases || stats.recent_cases.length === 0) && (
            <div className="p-8 text-center text-navy-500">
              No hay casos registrados aún
            </div>
          )}
        </motion.div>

        {/* FOIA Requests Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/admin/foia">
            <div className="card-elevated p-6 hover:border-blue-300 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileSearch className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-navy-900">
                      Solicitudes FOIA
                    </h3>
                    <p className="text-sm text-navy-500">
                      Clientes con visa negada que solicitaron información
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {foiaCount > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold">
                        {foiaCount}
                      </span>
                      <span className="text-sm text-amber-600 font-medium">pendientes</span>
                    </div>
                  )}
                  <ChevronRight className="w-5 h-5 text-navy-400" />
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Chat Center Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Link to="/admin/chat">
            <div className="card-elevated p-6 hover:border-purple-300 transition-colors cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <MessagesSquare className="w-7 h-7 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-navy-900">
                      Centro de Mensajes
                    </h3>
                    <p className="text-sm text-navy-500">
                      Conversaciones con clientes
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {chatCount > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold">
                        {chatCount}
                      </span>
                      <span className="text-sm text-purple-600 font-medium">sin leer</span>
                    </div>
                  )}
                  <ChevronRight className="w-5 h-5 text-navy-400" />
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Analytics Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Link to="/admin/analytics">
            <div className="card-elevated p-6 hover:border-gold-300 transition-colors cursor-pointer bg-gradient-to-r from-gold-50 to-amber-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-gold-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-7 h-7 text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-navy-900">
                      Analytics & Reportes
                    </h3>
                    <p className="text-sm text-navy-500">
                      Métricas detalladas, gráficos y estadísticas del sistema
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-navy-400" />
              </div>
            </div>
          </Link>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
